import { Cloud, Database, ShieldCheck, LifeBuoy, Building2, Network } from "lucide-react";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { FlowDiagram } from "@/components/ui/FlowDiagram";
import { TextField, TextAreaField } from "@/components/ui/form/FormField";
import { FormAlert } from "@/components/ui/form/FormAlert";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFormSubmit } from "@/hooks/useFormSubmit";

const seo = pageSeo(
  "Cloud Solutions — Infrastructure That Adapts to Your Business",
  "Design cloud environments for modern applications, distributed workloads, changing capacity requirements and business continuity."
);

const modules = [
  { icon: Cloud, title: "Cloud Compute", body: "Provision compute resources sized to your application, adjustable as demand changes." },
  { icon: Database, title: "Cloud Storage", body: "Scalable storage for application data, backups and archives." },
  { icon: LifeBuoy, title: "Backup Solutions", body: "Protect your data with a backup strategy suited to your recovery requirements." },
  { icon: ShieldCheck, title: "Disaster-Recovery Planning", body: "Plan for continuity so critical systems can recover when something goes wrong." },
  { icon: Building2, title: "Private Cloud", body: "A dedicated cloud environment for organisations with stricter isolation needs." },
  { icon: Network, title: "Hybrid Infrastructure", body: "Combine cloud and on-premises or dedicated resources into one architecture." },
];

type CloudFormValues = {
  name: string;
  email: string;
  phone: string;
  company: string;
  infrastructure: string;
  workload: string;
  storage: string;
  os: string;
  security: string;
  details: string;
};

function validate(values: CloudFormValues) {
  const errors: Partial<Record<keyof CloudFormValues, string>> = {};
  if (!values.name?.trim()) errors.name = "Please enter your name.";
  if (!values.email?.trim()) errors.email = "Please enter a business email.";
  else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Enter a valid email address.";
  if (!values.workload?.trim()) errors.workload = "Let us know the expected workload.";
  return errors;
}

export function CloudSolutionsPage() {
  useDocumentMeta(seo.title, seo.description);
  const { status, errors, handleSubmit } = useFormSubmit<CloudFormValues>(
    validate,
    "consultation_form_submit"
  );

  return (
    <>
      <PageHero
        eyebrow="Cloud Solutions"
        title="Cloud Infrastructure That Adapts to Your Business"
        description="Design cloud environments for modern applications, distributed workloads, changing capacity requirements and business continuity."
        primaryCta={{ label: "Request Cloud Consultation", href: "#consultation" }}
        secondaryCta={{ label: "Talk to an Expert", href: "/contact" }}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <SectionEyebrow>Cloud Modules</SectionEyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            Build the Cloud Environment You Need
          </h2>
          <p className="mt-4 text-sm text-text-secondary">
            Availability of individual modules may vary — confirm current options with our team.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <FeatureCard key={m.title} {...m} />
          ))}
        </div>
      </section>

      <section className="grid-backdrop border-y border-border bg-navy-secondary/40 py-20">
        <div className="mx-auto max-w-5xl px-5 text-center lg:px-8">
          <SectionEyebrow>How We Work Together</SectionEyebrow>
          <h2 className="mx-auto mt-5 max-w-xl font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            The Cloud Journey
          </h2>
          <div className="mt-14">
            <FlowDiagram steps={["Plan", "Design", "Deploy", "Monitor", "Scale"]} />
          </div>
        </div>
      </section>

      <section id="consultation" className="mx-auto max-w-3xl px-5 py-20 lg:px-8">
        <div className="text-center">
          <SectionEyebrow>Get Started</SectionEyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            Request a Cloud Consultation
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="glass-panel mt-10 space-y-5 rounded-card p-6 sm:p-10" noValidate>
          {/* honeypot — hidden from real users, catches simple bots */}
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          {status === "success" && <FormAlert status="success" />}
          {status === "error" && <FormAlert status="error" />}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="Name" name="name" required error={errors.name} />
            <TextField label="Business Email" name="email" type="email" required error={errors.email} />
            <TextField label="Phone" name="phone" type="tel" />
            <TextField label="Company" name="company" />
          </div>
          <TextField label="Existing Infrastructure" name="infrastructure" placeholder="e.g. currently on shared hosting, on-prem servers, another cloud provider" />
          <TextField label="Expected Workload" name="workload" required error={errors.workload} placeholder="e.g. web application, ecommerce platform, internal system" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="Storage Requirement" name="storage" placeholder="e.g. approximate data volume" />
            <TextField label="Preferred Operating System" name="os" placeholder="e.g. Linux, Windows, no preference" />
          </div>
          <TextField label="Security Requirements" name="security" placeholder="e.g. compliance needs, access control requirements" />
          <TextAreaField label="Additional Details" name="details" placeholder="Anything else that would help us understand your requirements" />

          <button
            type="submit"
            disabled={status === "submitting"}
            className="focus-ring w-full rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Request Cloud Consultation"}
          </button>
        </form>
      </section>
    </>
  );
}
